/*
=========================================================
* Material Kit 2 PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Icon from "@mui/material/Icon";
import MuiLink from "@mui/material/Link";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

function AssetLegalCard({ variant, color, icon, title, action }) {
  const buttonStyles = {
    width: "max-content",
    display: "flex",
    alignItems: "center",

    "& .material-icons-round": {
      fontSize: "1.125rem",
      transform: `translateX(3px)`,
      transition: "transform 0.2s cubic-bezier(0.34, 1.61, 0.7, 1.3)",
    },

    "&:hover .material-icons-round, &:focus .material-icons-round": {
      transform: `translateX(6px)`,
    },
  };

  let iconColor = color;

  if (variant === "gradient" && color !== "light") {
    iconColor = "white";
  } else if (variant === "gradient" && color === "light") {
    iconColor = "dark";
  }

  return (
    <MKBox
      display={{ xs: "block", md: "flex" }}
      variant={variant}
      bgColor={variant === "contained" ? "grey-100" : color}
      borderRadius="xl"
      pt={3.5}
      pb={3}
      px={3}
    >
      <MKTypography
        display="block"
        variant="h3"
        color={iconColor}
        textGradient={variant === "contained"}
        mt={-0.625}
      >
        {typeof icon === "string" ? <Icon>{icon}</Icon> : icon}
      </MKTypography>
      <MKBox pt={{ xs: 3, md: 0 }} pl={{ xs: 0, md: 2 }} lineHeight={1}>
        <MKTypography
          display="block"
          variant="5"
          color={variant === "contained" || color === "light" ? "dark" : "white"}
          fontWeight="bold"
          mb={1}
        >
          {title}
        </MKTypography>
        <MKTypography
          display="block"
          variant="body2"
          color={variant === "contained" || color === "light" ? "text" : "white"}
          mb={2}
        >
          Validez de precios 7 días, sujeto a disponibilidades y confirmación por propietarios. Se
          considera que la descripción y los precios indicados para las propiedades a la venta en
          este sitio web son correctos. No obstante, la información que contiene esta web puede ser
          sujeta a errores u omisiones. Las propiedades pueden ser sujetas a cambio de precio, venta
          o retirada del mercado. Los gastos adicionales de compra de la vivienda a cargo del
          comprador son: Gastos Notariales, Gastos de inscripción en el registro de la propiedad,
          impuestos vinculados a la compra. El porcentaje del ITP depende de cada comunidad y en
          Andalucía, este impuesto es de un 7% del precio de compra de la vivienda. Es posible
          obtener reducciones al pago de este impuesto, debido a que existen algunos tipos de
          bonificaciones que se aplican en algunos casos. En caso de compra de obra nueva se aplica
          el 10% de IVA y el 1.5% sobre Actos Jurídicos Documentados para nuevas propiedades
          compradas directamente al promotor. Adicionalmente, el comprador paga los gastos de
          Notaría y gastos de Inscripción de la escritura en el Registro de la Propiedad. Según el
          R.D. de la Junta de Andalucía 218/2005 del 11 de octubre, Usted tiene a su disposición
          copia de la correspondiente Ficha Informativa de esta propiedad en nuestra oficina
          principal de AVENIDA SIMON BOLIVIAR 17 MALAGA KINOS o en las oficinas de las Entidades
          financieras, promotoras o empresas con las que colabo
        </MKTypography>
        {action && action.type === "external" ? (
          <MKTypography
            component={MuiLink}
            href={action.route}
            target="_blank"
            rel="noreferrer"
            variant="body2"
            fontWeight="regular"
            color={variant === "contained" ? color : "white"}
            sx={buttonStyles}
          >
            {action.label} <Icon sx={{ fontWeight: "bold" }}>arrow_forward</Icon>
          </MKTypography>
        ) : null}
        {action && action.type === "internal" ? (
          <MKTypography
            component={Link}
            to={action.route}
            variant="body2"
            fontWeight="regular"
            color={variant === "contained" ? color : "white"}
            sx={buttonStyles}
          >
            {action.label} <Icon sx={{ fontWeight: "bold" }}>arrow_forward</Icon>
          </MKTypography>
        ) : null}
      </MKBox>
    </MKBox>
  );
}

// Setting default props for the AssetLegalCard
AssetLegalCard.defaultProps = {
  variant: "contained",
  color: "info",
  action: false,
};

// Typechecking props for the AssetLegalCard
AssetLegalCard.propTypes = {
  variant: PropTypes.oneOf(["contained", "gradient"]),
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  action: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal"]).isRequired,
      route: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ]),
};

export default AssetLegalCard;
